import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../layouts/MainLayout';
import WorkPageHeader from '../../components/work/WorkPageHeader';
import { Alert, Avatar, Button, Card, Form, Input, message, Result, Select, Space, Tag, Tooltip } from 'antd';
import { ExperimentOutlined, SendOutlined, ReloadOutlined, InboxOutlined, UserSwitchOutlined } from '@ant-design/icons';
import { liveChatApi, ForbiddenError } from '../../utils/api';
import { useSocket, useSocketEvent } from '../../hooks/useSocket';
import ChatMessageList from '../../components/live-chat/ChatMessageList';
import { SourceTag, LIVECHAT_SOCKET_EVENTS, operatorName } from '../../components/live-chat/liveChatMeta';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';

const randomContactId = () => `customer_${Math.random().toString(36).slice(2, 8)}`;

/**
 * شبیه‌ساز مشتری: پیام از طرف یک مخاطب ساختگی در منبع انتخابی وارد صندوق اپراتورها می‌شود
 * و پاسخ اپراتور (از طریق رویداد بلادرنگ یا همگام‌سازی دوره‌ای) همین‌جا نمایش داده می‌شود.
 */
const LiveChatTest = () => {
  const navigate = useNavigate();
  const { isConnected } = useSocket();
  const [form] = Form.useForm();
  const [forbidden, setForbidden] = useState(null);
  const [sources, setSources] = useState([]);
  const [recentContacts, setRecentContacts] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);

  const conversationIdRef = useRef(null);
  const messagesRef = useRef([]);
  conversationIdRef.current = conversationId;
  messagesRef.current = messages;

  const loadOptions = useCallback(async () => {
    try {
      const res = await liveChatApi.getTestOptions();
      const data = res?.data ?? {};
      setSources(data.sources ?? []);
      setRecentContacts(data.contacts ?? []);
      setForbidden(false);
      if (!form.getFieldValue('source_id') && data.sources?.length) {
        form.setFieldsValue({ source_id: data.sources[0].id });
      }
    } catch (error) {
      if (error instanceof ForbiddenError || error?.status === 403) setForbidden(error?.message || true);
      else message.error(error?.message || 'خطا در دریافت تنظیمات تست');
    }
  }, [form]);

  useEffect(() => {
    form.setFieldsValue({ contact_external_id: randomContactId(), contact_name: 'مشتری آزمایشی' });
    loadOptions();
  }, [form, loadOptions]);

  const fetchConversation = useCallback(async (full = false) => {
    const id = conversationIdRef.current;
    if (!id) return;
    const current = messagesRef.current;
    const afterId = !full && current.length ? current[current.length - 1].id : 0;
    try {
      const res = await liveChatApi.getTestConversation(id, afterId ? { after_id: afterId } : {});
      if (conversationIdRef.current !== id) return;
      setConversation(res?.data?.conversation ?? null);
      const items = res?.data?.messages ?? [];
      setMessages((prev) => {
        if (full) return items;
        const known = new Set(prev.map((m) => m.id));
        return [...prev, ...items.filter((m) => !known.has(m.id))];
      });
    } catch {
      // همگام‌سازی پس‌زمینه
    }
  }, []);

  useEffect(() => {
    if (!conversationId) return undefined;
    const interval = setInterval(() => fetchConversation(), isConnected ? 20000 : 5000);
    return () => clearInterval(interval);
  }, [conversationId, isConnected, fetchConversation]);

  const onSocketEvent = useCallback(
    (data) => {
      if (data?.conversation_id && Number(data.conversation_id) === Number(conversationIdRef.current)) {
        fetchConversation();
      }
    },
    [fetchConversation],
  );
  useSocketEvent(LIVECHAT_SOCKET_EVENTS.message, onSocketEvent, Boolean(conversationId));
  useSocketEvent(LIVECHAT_SOCKET_EVENTS.conversation, onSocketEvent, Boolean(conversationId));

  const send = async () => {
    const body = draft.trim();
    if (!body) return;
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSending(true);
    try {
      const res = await liveChatApi.sendTestMessage({ ...values, body });
      const newId = res?.data?.conversation_id;
      setDraft('');
      if (newId !== conversationIdRef.current) {
        conversationIdRef.current = newId;
        messagesRef.current = [];
        setConversationId(newId);
        setMessages([]);
        await fetchConversation(true);
      } else {
        await fetchConversation();
      }
    } catch (error) {
      message.error(error?.message || 'خطا در ارسال پیام آزمایشی');
    } finally {
      setSending(false);
    }
  };

  const startNewCustomer = () => {
    form.setFieldsValue({ contact_external_id: randomContactId(), contact_name: 'مشتری آزمایشی', contact_username: undefined, contact_phone: undefined });
    setConversationId(null);
    setConversation(null);
    setMessages([]);
  };

  const pickRecent = (contactId) => {
    const c = recentContacts.find((x) => x.id === contactId);
    if (!c) return;
    form.setFieldsValue({
      source_id: c.source_id,
      contact_external_id: String(c.external_id).replace(/^test:/, ''),
      contact_name: c.name,
      contact_username: c.username,
      contact_phone: c.phone,
    });
    setConversationId(null);
    setConversation(null);
    setMessages([]);
  };

  if (forbidden) {
    return (
      <MainLayout>
        <Result
          status="403"
          title={<span className="custom-font">شبیه‌ساز در دسترس نیست</span>}
          subTitle={<span className="custom-font">{typeof forbidden === 'string' ? forbidden : 'دسترسی تنظیمات گفتگوی آنلاین لازم است.'}</span>}
        />
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div style={{ fontFamily: 'CustomFont' }} className="custom-font">
        <Card className="shadow-xl border-0 work-page-card" styles={{ body: { padding: 0 } }}>
          <WorkPageHeader
            title="شبیه‌ساز مشتری (تست)"
            subtitle="ارسال پیام به‌جای مشتری از یک منبع دلخواه و مشاهده پاسخ اپراتور"
            icon={ExperimentOutlined}
            headerExtra={(
              <>
                <Button icon={<UserSwitchOutlined />} onClick={startNewCustomer} style={{ fontFamily: 'CustomFont' }}>مشتری جدید</Button>
                <Button icon={<InboxOutlined />} onClick={() => navigate('/live-chat')} style={{ fontFamily: 'CustomFont' }}>صندوق گفتگو</Button>
              </>
            )}
          />
          <div className="p-4 work-page-body rounded-b-xl">
        <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-4">
          <Card className="border-0 livechat-panel" title={<span className="custom-font">مشتری ساختگی</span>}>
            <Form form={form} layout="vertical" className="custom-font">
              <Form.Item name="source_id" label="منبع ورودی" rules={[{ required: true, message: 'منبع را انتخاب کنید' }]}>
                <Select
                  options={sources.map((s) => ({ value: s.id, label: <SourceTag source={s} /> }))}
                  onChange={() => {
                    setConversationId(null);
                    setConversation(null);
                    setMessages([]);
                  }}
                />
              </Form.Item>
              <Form.Item
                name="contact_external_id"
                label="شناسه مشتری در منبع"
                rules={[
                  { required: true, message: 'شناسه را وارد کنید' },
                  { pattern: /^[A-Za-z0-9_.@:+-]+$/, message: 'فقط حروف لاتین، عدد و _ . @ : + -' },
                ]}
                extra="شناسه یکسان = همان مشتری و همان گفتگوی باز"
              >
                <Input dir="ltr" maxLength={100} />
              </Form.Item>
              <Form.Item name="contact_name" label="نام نمایشی">
                <Input maxLength={191} />
              </Form.Item>
              <Form.Item name="contact_username" label="نام کاربری (اختیاری)">
                <Input dir="ltr" maxLength={191} placeholder="ali_insta" />
              </Form.Item>
              <Form.Item name="contact_phone" label="شماره تماس (اختیاری)">
                <Input dir="ltr" maxLength={50} />
              </Form.Item>
            </Form>
            {recentContacts.length > 0 && (
              <div>
                <div className="text-xs text-gray-500 mb-1">مشتری‌های آزمایشی اخیر</div>
                <Select
                  className="w-full"
                  placeholder="انتخاب برای ادامه گفتگو"
                  onChange={pickRecent}
                  value={null}
                  options={recentContacts.map((c) => ({
                    value: c.id,
                    label: `${c.name || c.username || c.external_id} — ${c.source?.name || ''}`,
                  }))}
                />
              </div>
            )}
          </Card>

          <div className="livechat-panel work-scroll-contain flex flex-col" style={{ height: 'calc(100vh - 250px)', minHeight: 420 }}>
            <div className="livechat-panel-bar px-4 py-3 border-b flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Avatar style={{ backgroundColor: '#0D887E' }}>پ</Avatar>
                <div>
                  <div className="font-bold text-gray-800 dark:text-gray-100">پشتیبانی</div>
                  <div className="text-xs text-gray-500">
                    {conversation ? (
                      <>
                        گفتگو #{conversation.id} · {conversation.status === 'open' ? 'باز' : 'بسته'}
                        {conversation.assigned_operator && <> · اپراتور: {operatorName(conversation.assigned_operator)}</>}
                      </>
                    ) : (
                      'برای شروع، اولین پیام را بفرستید'
                    )}
                  </div>
                </div>
              </div>
              <Space>
                {conversation?.source && <SourceTag source={conversation.source} />}
                <Tag color="purple" className="custom-font">تست</Tag>
                {conversationId && (
                  <Tooltip title="بارگذاری مجدد">
                    <Button size="small" icon={<ReloadOutlined />} onClick={() => fetchConversation(true)} />
                  </Tooltip>
                )}
              </Space>
            </div>
            <div className="work-scroll-pane flex-1 min-h-0 livechat-thread-bg">
              <ChatMessageList key={conversationId || 'new'} messages={messages} perspective="customer" />
            </div>
            <div className="livechat-panel-bar p-3 border-t">
              {conversation?.status === 'closed' && (
                <Alert type="warning" showIcon className="custom-font mb-2" title="اپراتور این گفتگو را بسته است؛ پیام بعدی یک گفتگوی جدید می‌سازد." />
              )}
              <div className="flex gap-2 items-end">
                <Input.TextArea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onPressEnter={(e) => {
                    if (!e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  autoSize={{ minRows: 1, maxRows: 5 }}
                  maxLength={5000}
                  placeholder="پیام مشتری…"
                  className="custom-font"
                />
                <Button type="primary" icon={<SendOutlined />} onClick={send} loading={sending} disabled={!draft.trim()} className={PANEL_PRIMARY_BTN_CLASS} />
              </div>
            </div>
          </div>
        </div>
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default LiveChatTest;
